import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useMcMembers } from '@/hooks/useMcMembers';
import { numberLocale } from '@/lib/language';

interface McMembersCardProps {
  /** `merchant_centers.id`, dieselbe MC, unter der die Karte gerendert wird. */
  mcId: string;
  /** `auth.uid()` des angemeldeten Nutzers -- entscheidet, welche Zeile den
   *  Uebertragen-Knopf traegt. `undefined`/`null` heisst "noch kein User
   *  geladen": dann traegt keine Zeile den Knopf, statt einen zu raten. */
  currentUserId: string | null | undefined;
}

/**
 * Task 11: Mitglieder-Karte je Merchant Center (Migration
 * `20260923110000_merchant_center_members.sql`, Spec
 * `docs/superpowers/specs/2026-09-23-merchant-center-mitgliedschaft-design.md`).
 * Wird von `MerchantCenters.tsx` in die `CardContent` jeder MC-Karte
 * eingehaengt -- sichtbar fuer Eigentuemer UND Mitglieder, aber nur der
 * Eigentuemer sieht je Mitglied den Knopf "Eigentuemerschaft uebertragen".
 *
 * Kein shadcn `<Card>`-Wrapper (die MC-Karte selbst ist schon eine) -- Muster
 * aus `SubscriptionRetentionCard.tsx`: ein einfacher umrandeter Block statt
 * einer verschachtelten Karte in einer Karte.
 *
 * `onClick`-Stop am aeussersten Block: die umschliessende MC-Karte in
 * `MerchantCenters.tsx` traegt selbst einen `onClick`, der die MC auswaehlt.
 * Ohne den Stop wuerde ein Klick auf "Eigentuemerschaft uebertragen" (oder
 * jeden anderen Knopf hier) die Auswahl der MC mit ausloesen -- dieselbe
 * Absicherung, die dort schon fuer den Website-Link und den
 * "Mehr anzeigen"-Knopf gilt.
 */
export function McMembersCard({ mcId, currentUserId }: McMembersCardProps) {
  const { t, i18n } = useTranslation('settings');
  const queryClient = useQueryClient();
  const { members, isLoading } = useMcMembers(mcId);

  if (isLoading || members.length === 0) {
    return null;
  }

  const isOwner = members.some((member) => member.user_id === currentUserId && member.role === 'owner');

  const handleTransfer = async (targetUserId: string) => {
    const { data, error } = await supabase.rpc('transfer_merchant_center_ownership', {
      p_mc_id: mcId,
      p_new_owner_user_id: targetUserId,
    });

    if (error || data !== true) {
      toast.error(t('merchantCenters.members.transferFailed'));
      return;
    }

    toast.success(t('merchantCenters.members.transferDone'));
    queryClient.invalidateQueries({ queryKey: ['mc-members', mcId] });
    queryClient.invalidateQueries({ queryKey: ['merchant-centers'] });
    queryClient.invalidateQueries({ queryKey: ['enforcement'] });
  };

  return (
    <div
      data-testid="mc-members-card"
      className="mt-3 rounded-md border border-border bg-muted/40 p-3 space-y-2"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-xs font-medium text-muted-foreground">{t('merchantCenters.members.title')}</p>
      <ul className="space-y-2">
        {members.map((member) => (
          <li key={member.user_id} className="flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="truncate">
                  {member.email}
                  {member.user_id === currentUserId && (
                    <span className="text-muted-foreground"> ({t('merchantCenters.members.you')})</span>
                  )}
                </span>
                {member.role === 'owner' && <Badge variant="outline">{t('merchantCenters.members.owner')}</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('merchantCenters.members.joined', {
                  date: new Date(member.joined_at).toLocaleDateString(numberLocale(i18n.language)),
                })}
              </p>
            </div>
            {isOwner && member.role !== 'owner' && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    {t('merchantCenters.members.transfer')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('merchantCenters.members.transferTitle', { email: member.email })}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('merchantCenters.members.transferBody', { email: member.email })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('actions.cancel', { ns: 'common' })}</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleTransfer(member.user_id)}>
                      {t('merchantCenters.members.transferConfirm')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
